import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

export interface AppUser {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  avatar?: string;
}

const domain = (import.meta.env.VITE_COGNITO_DOMAIN || '').replace(/^https?:\/\//, '');
const appUrl =
  import.meta.env.VITE_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// Initialize Amplify Cognito Auth
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
      loginWith: domain
        ? {
            oauth: {
              domain,
              scopes: ['openid', 'email', 'profile'],
              redirectSignIn: [appUrl],
              redirectSignOut: [appUrl],
              responseType: 'code',
            },
          }
        : undefined,
    },
  },
});

async function toAppUser(): Promise<AppUser> {
  const current = await getCurrentUser();
  const attrs = await fetchUserAttributes();
  return {
    id: current.userId,
    email: attrs.email || '',
    displayName: attrs.name || attrs.email || current.username,
    emailVerified: attrs.email_verified === 'true',
    avatar: attrs.picture,
  };
}

export async function registerWithEmail(
  email: string,
  password: string,
  displayName: string,
): Promise<{ needsConfirmation: boolean }> {
  const { isSignUpComplete } = await signUp({
    username: email,
    password,
    options: { userAttributes: { email, name: displayName } },
  });
  return { needsConfirmation: !isSignUpComplete };
}

export async function confirmRegistration(email: string, code: string): Promise<void> {
  await confirmSignUp({ username: email, confirmationCode: code });
}

export async function resendConfirmation(email: string): Promise<void> {
  await resendSignUpCode({ username: email });
}

export async function loginWithEmail(email: string, password: string): Promise<AppUser> {
  await signIn({ username: email, password });
  return toAppUser();
}

export async function loginWithGoogle(): Promise<void> {
  await signInWithRedirect({ provider: 'Google' });
}

// signInWithRedirect leaves the SPA entirely and returns on a fresh page
// load once Cognito completes the OAuth round trip, so there is no in-memory
// call site left to decide "this was a signup" once control comes back.
// sessionStorage survives that redirect in the same tab, so the Continue
// with Google button on the signup screen marks intent before leaving, and
// RedirectIfAuthed (SiteApp.tsx) reads and clears it once the user reappears
// - the same signup-vs-signin distinction the email/password flow gets for
// free from being two separate forms.
const SIGNUP_INTENT_KEY = 'ss-signup-intent';

export function markSignupIntent(): void {
  try {
    sessionStorage.setItem(SIGNUP_INTENT_KEY, '1');
  } catch {
    // Best effort - a blocked sessionStorage just means this Google sign-up
    // lands on the dashboard instead of onboarding, not a broken sign-up.
  }
}

/** True once, then clears itself - a second read after a redirect returns false. */
export function consumeSignupIntent(): boolean {
  try {
    const was = sessionStorage.getItem(SIGNUP_INTENT_KEY) === '1';
    if (was) sessionStorage.removeItem(SIGNUP_INTENT_KEY);
    return was;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  await signOut();
}

export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({ username: email });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

export async function currentUser(): Promise<AppUser | null> {
  try {
    return await toAppUser();
  } catch {
    return null;
  }
}

export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export function onAuthChange(callback: (user: AppUser | null) => void): () => void {
  void currentUser().then(callback);
  return Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signedIn' || payload.event === 'tokenRefresh') {
      void currentUser().then(callback);
    } else if (payload.event === 'signedOut') {
      callback(null);
    }
  });
}
